<template>
  <div class="relative">
    <ClientOnly>
      <div
        v-if="!isMobile"
        ref="galleryContainer"
        class="grid grid-cols-2 gap-6"
        :class="[
          { '!grid-rows-2 !gap-3': gallerySliced.length > 2 },
          { '!grid-cols-[_1fr,_20%_20%]': !showFullSize && gallerySliced.length > 2 },
          { '!grid-cols-[_1fr,_1fr_20%_20%]': showFullSize },
        ]"
        tabindex="0"
        @keydown="handleKeyDown"
      >
        <div
          v-for="(item, index) in gallerySliced"
          :key="index"
          class="relative overflow-hidden rounded-2xl bg-black-05 cursor-pointer group"
          :class="[
            { 'row-span-2': index === 0 && gallerySliced.length > 2 },
            { 'row-span-2': index === 1 && showFullSize },
          ]"
          @click="showGalleryPopup(getImageIndexInFullGallery(index))"
        >
          <template v-if="item.isVideo">
            <video
              :src="item.raw"
              :poster="item.thumb || undefined"
              muted
              loop
              playsinline
              autoplay
              preload="metadata"
              :class="[
                'h-[370px] w-full object-cover hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-gold-primary-100',
                { '!h-[179px]': index !== 0 && gallerySliced.length > 2 },
                { '!h-[179px]': index !== 1 && showFullSize && index !== 0 },
              ]"
            />
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                class="flex h-14 w-14 items-center justify-center rounded-full bg-black/60 backdrop-blur-sm group-hover:bg-black/70 transition-colors"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="white"
                  aria-hidden="true"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </template>
          <img
            v-else
            :src="item.thumb"
            :alt="`preview-${index}`"
            :loading="index === 0 ? 'eager' : 'lazy'"
            decoding="async"
            :class="[
              'h-[370px] w-full object-cover hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-gold-primary-100',
              { '!h-[179px]': index !== 0 && gallerySliced.length > 2 },
              { '!h-[179px]': index !== 1 && showFullSize && index !== 0 },
            ]"
            @error="onThumbError($event, item.raw)"
          />
        </div>
      </div>
      <div
        v-else
        class="relative"
      >
        <!-- Position counter: on a phone only one photo shows, so say how many there are. -->
        <span
          v-if="galleryItems.length > 1"
          class="pointer-events-none absolute end-[9%] top-3 z-10 rounded-full bg-black/60 px-2.5 py-1 text-[12px] font-semibold text-white"
          dir="ltr"
          >{{ mobileSlideIndex + 1 }} / {{ galleryItems.length }}</span
        >
        <Swiper
          :loop="galleryItems.length > 1"
          :slides-per-view="1.1"
          space-between="8px"
          :centered-slides="true"
          :modules="[Navigation, Pagination]"
          :pagination="{ dynamicBullets: true, clickable: true }"
          class="gallery-mobile-swiper h-auto !overflow-y-visible pb-6"
          @slide-change="onMobileSlideChange"
        >
          <SwiperSlide
            v-for="(item, index) in galleryItems"
            :key="index"
            class="h-auto cursor-pointer select-none relative"
            @click="showGalleryPopup(index)"
          >
            <template v-if="item.isVideo">
              <video
                :src="item.raw"
                :poster="item.thumb || undefined"
                muted
                loop
                autoplay
                playsinline
                preload="metadata"
                class="aspect-[4/3] w-full rounded-xl object-cover"
              />
              <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div class="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 backdrop-blur-sm">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="white"
                    aria-hidden="true"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </template>
            <img
              v-else
              :src="item.thumb"
              :alt="`preview-${index}`"
              :loading="index === 0 ? 'eager' : 'lazy'"
              decoding="async"
              class="aspect-[4/3] w-full rounded-xl object-cover"
              @error="onThumbError($event, item.raw)"
            />
          </SwiperSlide>
        </Swiper>
      </div>
      <UIButton
        v-show="!isAnyPopupOpen"
        id="view-photos"
        variant="secondary"
        class="mx-auto mt-2 !w-[calc(100%-32px)] md:!absolute md:bottom-4 md:end-4 md:z-10 md:rounded-xl md:!bg-white md:px-4 md:py-2 md:mt-0 md:!w-auto"
        @click="showGalleryPopup"
      >
        {{ t('common.actions.viewAll') }} ({{ galleryItems.length }})
      </UIButton>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { ImageAlbum, PropertyDetailed } from '~/types/properties';
import { POPUPS } from '@/components/dynamic/maps';
import 'swiper/css';
import 'swiper/css/pagination';
import { checkComingSoonImage, COMING_SOON_PLACEHOLDER } from '~/utils/comingSoonImages';
import { isVideoUrl } from '~/utils/mediaType';
import { resizedImageUrl } from '~/utils/resizedImageUrl';

const uiStore = useUIStore();
const { t } = useI18n();

// Hide the floating "View all" button while a popup is open — the full-photos
// modal is a fixed overlay, but this absolutely-positioned z-10 button can
// otherwise paint above it (stacking-context quirk). Simplest robust fix.
const isAnyPopupOpen = computed(() => Boolean(uiStore.dynamicPopup?.value));

type Props = {
  item: PropertyDetailed;
  fullSize?: boolean;
};

const { isMobile, isLargeScreen } = useAppBreakpoints();

const props = defineProps<Props>();

const galleryContainer = ref<HTMLElement | null>(null);
const currentImageIndex = ref(0);
// Slide shown in the phone slider (realIndex: the loop clones don't count).
const mobileSlideIndex = ref(0);
const onMobileSlideChange = (swiper: { realIndex: number }) => {
  mobileSlideIndex.value = swiper.realIndex;
};
const comingSoonUrls = ref<Set<string>>(new Set());

const thumbDimensions = computed((): readonly [number, number] => {
  if (isMobile) return [720, 540];
  if (isLargeScreen.value) return [1280, 960];
  return [1024, 768];
});

const fullDimensions = computed((): readonly [number, number] => {
  if (isMobile) return [1200, 900];
  if (isLargeScreen.value) return [1600, 1200];
  return [1400, 1050];
});

const rawImages = computed(() => {
  const additionalPhotos = props.item.albums.flatMap((item: ImageAlbum) => item.images);
  const propertyVideos = ((props.item as { videos?: Array<{ src?: string }> }).videos ?? [])
    .map((v) => v?.src)
    .filter((s): s is string => Boolean(s));
  // Combine album images, extra-album images, and the documents-categorized
  // videos, then DEDUP. Videos can live in EITHER the album OR videos[], so we
  // partition the whole combined list and surface ALL videos FIRST — the hero
  // slot (and the main sliced view) always leads with the autoplay preview.
  const seen = new Set<string>();
  const all = [...props.item.album, ...additionalPhotos, ...propertyVideos].filter(
    (u): u is string => Boolean(u) && !seen.has(u) && Boolean(seen.add(u)),
  );
  const videos = all.filter((u) => isVideoUrl(u));
  if (!videos.length) return all;
  const images = all.filter((u) => !isVideoUrl(u));
  return [...videos, ...images];
});

type GalleryItem = { thumb: string; full: string; raw: string; isVideo: boolean };

const galleryItems = computed((): GalleryItem[] => {
  const thumbDims = thumbDimensions.value;
  const popupDims = fullDimensions.value;
  return rawImages.value.map((url: string) => {
    // Videos can't be CDN-resized — emit the raw URL and let <video> handle
    // it. Thumb is left empty so the browser renders the first frame as
    // poster automatically.
    if (isVideoUrl(url)) {
      return { thumb: '', full: url, raw: url, isVideo: true };
    }
    if (comingSoonUrls.value.has(url)) {
      return { thumb: COMING_SOON_PLACEHOLDER, full: COMING_SOON_PLACEHOLDER, raw: url, isVideo: false };
    }
    return {
      thumb: resizedImageUrl(url, thumbDims),
      full: resizedImageUrl(url, popupDims),
      raw: url,
      isVideo: false,
    };
  });
});

const onThumbError = (event: Event, rawUrl: string) => {
  const img = event.target as HTMLImageElement | null;
  if (!img || img.dataset.fallbackApplied === '1') return;
  img.dataset.fallbackApplied = '1';
  img.removeAttribute('srcset');
  img.src = rawUrl;
};

const checkImages = async () => {
  for (const url of rawImages.value) {
    if (isVideoUrl(url)) continue;
    try {
      const result = await checkComingSoonImage(url);
      if (result) {
        comingSoonUrls.value = new Set([...comingSoonUrls.value, url]);
      }
    } catch {}
  }
};

const showFullSize = computed(() => props.fullSize && galleryItems.value.length > 5);

const gallerySliced = computed(() => {
  const items = galleryItems.value;
  if (showFullSize.value) return items.slice(0, 6);
  if (!props.fullSize && items.length >= 5) return items.slice(0, 5);
  return items.slice(0, 2);
});

const getImageIndexInFullGallery = (slicedIndex: number) => {
  // Match on `raw` (the unique URL) — videos all share an empty `thumb`, so
  // matching by thumb would always resolve to the first video.
  const slicedItem = gallerySliced.value[slicedIndex];
  if (!slicedItem) return 0;
  return galleryItems.value.findIndex((item) => item.raw === slicedItem.raw);
};

const showGalleryPopup = (initialIndex: number = 0) => {
  const items = galleryItems.value;
  uiStore.showPopup(POPUPS.PROPERTY_FULL_PHOTOS, {
    item: props.item,
    gallery: items.map((i) => i.full),
    rawGallery: items.map((i) => i.raw),
    initialIndex: initialIndex >= 0 ? initialIndex : 0,
  });
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (isMobile) return;

  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    if (currentImageIndex.value > 0) {
      currentImageIndex.value--;
    } else {
      currentImageIndex.value = galleryItems.value.length - 1;
    }
    showGalleryPopup(currentImageIndex.value);
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    if (currentImageIndex.value < galleryItems.value.length - 1) {
      currentImageIndex.value++;
    } else {
      currentImageIndex.value = 0;
    }
    showGalleryPopup(currentImageIndex.value);
  }
};

onMounted(() => {
  if (!isMobile && galleryContainer.value) {
    galleryContainer.value.focus();
  }
  checkImages();
});

// Watch the project IDENTITY, not the whole object deeply. The detail route
// swaps `property` to the authed view on mount (same project, same gallery —
// redaction never touches album/gallery), and a deep watch would re-run this
// on that swap, re-rendering the Swiper subtree and tripping a null-vnode
// unmount crash. Keying on complex_id only re-inits when the project truly
// changes (SPA nav between two detail pages).
watch(
  () => (props.item as { complex_id?: string })?.complex_id,
  () => {
    currentImageIndex.value = 0;
    mobileSlideIndex.value = 0;
    comingSoonUrls.value = new Set();
    checkImages();
  },
);
</script>

<style scoped lang="scss">
// Dots under the phone slider, in the brand gold rather than Swiper's default blue.
.gallery-mobile-swiper :deep(.swiper-pagination) {
  bottom: 0;
}

.gallery-mobile-swiper :deep(.swiper-pagination-bullet-active) {
  background: #eda436;
}
</style>
